import {
  LogicalOperatorRules,
  type Condition,
  ConditionalSection,
  LogicalOperators,
  type Validation,
} from "@/providers/LenderCardContextProvider/types/LenderProductTypes";
import type {
  Asset,
  Customer,
  CustomerDisclaimer,
  Entity,
  LoanDetails,
} from "@/store/slices/types/applicationFormSlice";
// @ts-expect-error: ignoring as it is existing js file
import regex from "@/utils/regex";
import {
  getIsPersonalFinanceList,
  getIsRequiredFieldEmpty,
  getMissingPersonalFinanceItemIndex,
  getShouldValidatePersonalFinanceItem,
} from "./requiredFieldValues";

export const invalidKey = "isInvalid";

const indexedSubSectionOverrides: Record<string, string> = {
  customerAssets: "customerAssets[0]",
};

export const getFieldValue = (
  obj: Record<string, unknown> | Array<Record<string, unknown>>,
  fieldPath: string,
): unknown => {
  const keys = fieldPath.split(".");
  let current: unknown = obj;
  // Get last item if obj is an array.
  if (Array.isArray(obj)) {
    current = obj[obj.length - 1];
  }
  for (const key of keys) {
    if (current && typeof current === "object" && key in current) {
      current = (current as Record<string, unknown>)[key];
    } else {
      return undefined;
    }
  }
  return current;
};

export const getTargetFieldName = ({ subSection, targetField }: { subSection: string; targetField: string }) => {
  if (subSection === "self") {
    return targetField;
  }
  return `${subSection}.${targetField}`;
};

const isNumberType = (value: unknown): boolean => {
  return !Number.isNaN(Number(value));
};

const convertRuleToFunction = (rule: LogicalOperatorRules): ((value: unknown, ruleValue?: unknown) => boolean) => {
  const ruleFunctions: Record<LogicalOperatorRules, (value: unknown, ruleValue?: unknown) => boolean> = {
    [LogicalOperatorRules.IS_EQUAL]: (value, ruleValue) => {
      if (isNumberType(ruleValue)) {
        return Number(value) === Number(ruleValue);
      }
      return value === ruleValue;
    },
    [LogicalOperatorRules.IS_NOT_EQUAL]: (value, ruleValue) => {
      if (isNumberType(ruleValue)) {
        return Number(value) !== Number(ruleValue);
      }
      return value !== ruleValue;
    },
    [LogicalOperatorRules.IS_GREATER_THAN]: (value, ruleValue) => {
      if (isNumberType(ruleValue)) {
        return Number(value) > Number(ruleValue);
      }
      return false;
    },
    [LogicalOperatorRules.IS_LESS_THAN]: (value, ruleValue) => {
      if (isNumberType(ruleValue)) {
        return Number(value) < Number(ruleValue);
      }
      return false;
    },
    [LogicalOperatorRules.IS_EMPTY]: (value) => {
      if (value === null || value === undefined) {
        return true;
      }
      if (typeof value === "string") {
        return value.trim() === "";
      }
      if (Array.isArray(value)) {
        return value.length === 0;
      }
      if (typeof value === "object") {
        return Object.keys(value).length === 0;
      }
      return false;
    },
    [LogicalOperatorRules.IS_NOT_EMPTY]: (value) => {
      if (value === null || value === undefined) {
        return false;
      }
      if (typeof value === "string") {
        return value.trim() !== "";
      }
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      if (typeof value === "object") {
        return Object.keys(value).length > 0;
      }
      return true;
    },
  };

  return ruleFunctions[rule];
};

export const evaluateCondition = ({
  rule,
  fieldValue,
  ruleValue,
}: {
  rule: LogicalOperatorRules;
  fieldValue: unknown;
  ruleValue?: unknown;
}): boolean => {
  return convertRuleToFunction(rule)(fieldValue, ruleValue);
};

export const evaluateConditionsBySection = ({
  conditions,
  applicationSections,
}: {
  conditions?: Condition[];
  applicationSections: {
    customers: Customer[];
    asset: Asset;
    entities: Entity[];
    loanDetails: LoanDetails;
  };
}): (string | boolean)[] => {
  if (!conditions?.length) {
    return [true];
  }

  const evaluatedConditions = conditions.map((condition) => {
    // No condition.
    if (!condition) {
      return false;
    }

    const { section, subSection, sourceField, rule, ruleValue } = condition;
    // No section, subSection or sourceField.
    if (!section || !subSection || !sourceField) {
      return false;
    }

    switch (section) {
      case ConditionalSection.AGREEMENT: {
        const primaryCustomerDisclaimer = applicationSections.customers[0]?.customerDisclaimer;
        const sourceSubSection =
          subSection === "self"
            ? primaryCustomerDisclaimer
            : primaryCustomerDisclaimer?.[subSection as keyof CustomerDisclaimer];
        const sourceFieldValue = getFieldValue(sourceSubSection as unknown as Record<string, unknown>, sourceField);

        return evaluateCondition({ rule, fieldValue: sourceFieldValue, ruleValue });
      }
      case ConditionalSection.ASSET: {
        // Get source sub section.
        const sourceSubSection =
          subSection === "self" ? applicationSections.asset : applicationSections.asset[subSection as keyof Asset];
        // Get source field value.
        const sourceFieldValue = getFieldValue(sourceSubSection as unknown as Record<string, unknown>, sourceField);

        // Evaluate source field value against rule and rule value.
        return evaluateCondition({ rule, fieldValue: sourceFieldValue, ruleValue });
      }
      case ConditionalSection.CUSTOMER: {
        const customerConditionMet = applicationSections.customers.filter((customer) => {
          // Get source sub section.
          const sourceSubSection = subSection === "self" ? customer : customer[subSection as keyof Customer];
          // Get source field value.
          const sourceFieldValue = getFieldValue(sourceSubSection as unknown as Record<string, unknown>, sourceField);

          // Evaluate source field value against rule and rule value.
          return evaluateCondition({ rule, fieldValue: sourceFieldValue, ruleValue });
        });
        return customerConditionMet.map((customer) => customer._id);
      }
      case ConditionalSection.ENTITY: {
        const entityConditionMet = applicationSections.entities.filter((entity) => {
          // Get source sub section.
          const sourceSubSection = subSection === "self" ? entity : entity[subSection as keyof Entity];
          // Get source field value.
          const sourceFieldValue = getFieldValue(sourceSubSection as unknown as Record<string, unknown>, sourceField);

          // Evaluate source field value against rule and rule value.
          return evaluateCondition({ rule, fieldValue: sourceFieldValue, ruleValue });
        });
        return entityConditionMet.map((entity) => entity._id);
      }
      case ConditionalSection.LOAN_DETAILS: {
        // Get source sub section.
        const sourceSubSection =
          subSection === "self"
            ? applicationSections.loanDetails
            : applicationSections.loanDetails[subSection as keyof LoanDetails];

        // Get source field value.
        const sourceFieldValue = getFieldValue(sourceSubSection as unknown as Record<string, unknown>, sourceField);

        // Evaluate source field value against rule and rule value.
        return evaluateCondition({ rule, fieldValue: sourceFieldValue, ruleValue });
      }
      default: {
        return false;
      }
    }
  });
  return evaluatedConditions.flat();
};

export const evaluateByLogicalOperators = ({
  evaluatedList: evaluatedList,
  logicalOperators,
}: {
  evaluatedList: (string | boolean)[];
  logicalOperators: LogicalOperators;
}): boolean => {
  // If the condition type is ANY, return true if any of the conditions are true.
  if (logicalOperators === LogicalOperators.ANY) {
    return evaluatedList.some((condition) => !!condition);
  }
  // Else if the condition type is ALL, return true if all of the conditions are true.
  return evaluatedList.every((condition) => !!condition);
};

export const evaluateValidations = ({
  validations,
  targetFieldValue,
  aggregatedTargetFieldValue,
}: {
  validations: Validation[];
  targetFieldValue: unknown;
  aggregatedTargetFieldValue?: number;
}): boolean[] => {
  const evaluatedValidations = validations.map((validation) => {
    const { rule, ruleValue, aggregation } = validation;
    const fieldValue =
      aggregation === "sum" && aggregatedTargetFieldValue !== undefined ? aggregatedTargetFieldValue : targetFieldValue;
    return evaluateCondition({ rule, fieldValue, ruleValue });
  });
  return evaluatedValidations;
};

const sumTargetFieldValues = ({
  targetSubSection,
  targetField,
}: {
  targetSubSection: unknown[];
  targetField: string;
}): number => {
  return targetSubSection.reduce<number>((total, subSection) => {
    const value = getFieldValue(subSection as Record<string, unknown>, targetField);
    const numericValue =
      (typeof value === "number" || (typeof value === "string" && value.trim() !== "")) &&
      Number.isFinite(Number(value))
        ? Number(value)
        : 0;
    return total + numericValue;
  }, 0);
};

export const getHasInvalidError = ({ errors, fieldName }: { errors: Record<string, string>; fieldName: string }) => {
  return !!Object.keys(errors ?? {}).find((key) => regex.invalid.test(key) && key.includes(fieldName));
};

export const getInvalidErrorValue = ({ errors, fieldName }: { errors: Record<string, string>; fieldName: string }) => {
  const matchedKey = Object.keys(errors ?? {}).find((key) => regex.invalid.test(key) && key.includes(fieldName));
  return matchedKey ? errors[matchedKey] : undefined;
};

const getIsConditionMetForItem = (evaluatedConditions: (string | boolean)[], item: { _id: string }) => {
  return (
    (evaluatedConditions ?? []).some((condition) => typeof condition === "string" && condition === item._id) ||
    (evaluatedConditions ?? []).some((condition) => typeof condition === "boolean" && !!condition)
  );
};

export const getConditionalFields = ({
  validationRequired,
  targetSubSection,
  targetField,
  subSection,
  item,
  validationType,
  validationMessage,
  validations,
  skipIdentifier = false, // Mainly for agreement fields.
  evaluatedConditions,
}: {
  validationRequired: boolean;
  targetSubSection: unknown;
  targetField: string;
  subSection: string;
  item: { _id: string };
  validationType: LogicalOperators;
  validationMessage?: string;
  validations: Validation[];
  skipIdentifier?: boolean;
  evaluatedConditions?: (string | boolean)[];
}) => {
  const fields = { remaining: [] as string[], all: [] as string[] };
  // Agreement fields do not need to be appended with an identifier.
  // Agreement fields look up the first customer.
  const identifier = skipIdentifier ? "" : `_${item._id}`;

  // Check if target sub section's value is an array.
  const isTargetSubSectionArray = Array.isArray(targetSubSection);

  const isConditionMetForItem = getIsConditionMetForItem(evaluatedConditions ?? [], item);

  if (isConditionMetForItem) {
    // If the value of subSection is an array, then we need to check if the target field is valid for all the sub sections.
    // Else, get the field value which will be a string.
    if (isTargetSubSectionArray) {
      const indexedSubSection = indexedSubSectionOverrides[subSection] ?? `${subSection}.[0]`;
      const targetFieldName = `${indexedSubSection}.${targetField}`;

      // If validation is required, then we need to check if the target field is valid for all the sub sections.
      // Else, just check if the value in the list of target field values has a truthy value (not null or undefined).
      if (validationRequired) {
        // On a personal finance list, untouched rows after the first are not validated.
        const getIsRowValidated = (targetItem: Record<string, unknown> | undefined, index: number) =>
          !getIsPersonalFinanceList(subSection) ||
          getShouldValidatePersonalFinanceItem({ listName: subSection, item: targetItem, index });
        const rowsToValidate = targetSubSection.filter(getIsRowValidated);
        const hasSumAggregation = validations.some((validation) => validation.aggregation === "sum");
        const aggregatedTargetFieldValue = hasSumAggregation
          ? sumTargetFieldValues({ targetSubSection: rowsToValidate, targetField })
          : undefined;
        // An aggregate validation still needs to evaluate an empty array against a total of zero.
        const subSectionsToValidate = rowsToValidate.length > 0 || !hasSumAggregation ? rowsToValidate : [undefined];

        // Evaluate the validations against the list of target field values.
        const isInvalidFields = subSectionsToValidate.map((subSection) => {
          const targetFieldValue = getFieldValue(subSection as Record<string, unknown>, targetField);
          const evaluatedValidations = evaluateValidations({
            validations: validations ?? [],
            targetFieldValue,
            aggregatedTargetFieldValue,
          });

          // With the evaluated validations, evaluate if the validations meet the validation type (ALL or ANY).
          const isInvalidField = !evaluateByLogicalOperators({
            evaluatedList: evaluatedValidations,
            logicalOperators: validationType,
          });
          return isInvalidField;
        });

        // If all the evaluated target field values are invalid, then the target field is invalid.
        const everyFieldIsInvalid = isInvalidFields.every((isInvalid) => isInvalid);
        // The error shows on the last validated row.
        const lastTargetIndex = targetSubSection.findLastIndex(getIsRowValidated);
        if (everyFieldIsInvalid) {
          fields.remaining.push(
            `${targetFieldName}${identifier}_${lastTargetIndex}_${invalidKey}=${validationMessage}`,
          );
        }

        fields.all.push(`${targetFieldName}${identifier}_${lastTargetIndex}_${invalidKey}=${validationMessage}`);
      } else if (getIsPersonalFinanceList(subSection)) {
        const invalidIndex = getMissingPersonalFinanceItemIndex({
          listName: subSection,
          items: targetSubSection,
          fieldPath: targetField,
        });

        if (invalidIndex >= 0) {
          fields.remaining.push(`${targetFieldName}${identifier}_${invalidIndex}`);
        }

        fields.all.push(`${targetFieldName}${identifier}_${invalidIndex >= 0 ? invalidIndex : 0}`);
      } else {
        // Get the last index of the sub section with a populated target field.
        const lastIndexOfSubSectionWithValidTargetField = targetSubSection.findLastIndex((subSection) => {
          return !getIsRequiredFieldEmpty(getFieldValue(subSection as unknown as Record<string, unknown>, targetField));
        });

        const invalidIndex = lastIndexOfSubSectionWithValidTargetField + 1;

        if (invalidIndex < targetSubSection.length) {
          fields.remaining.push(`${targetFieldName}${identifier}_${invalidIndex}`);
        }

        fields.all.push(`${targetFieldName}${identifier}_${invalidIndex}`);
      }
    } else {
      // Construct target field name.
      const targetFieldName = getTargetFieldName({ subSection, targetField });

      // Get target field value.
      const targetFieldValue = getFieldValue(targetSubSection as unknown as Record<string, unknown>, targetField);

      if (validationRequired) {
        // Evaluate the validations against the target field value.
        const evaluatedValidations = evaluateValidations({
          validations: validations ?? [],
          targetFieldValue,
        });

        // With the evaluated validations, evaluate if the validations meet the validation type (ALL or ANY).
        const isInvalidField = !evaluateByLogicalOperators({
          evaluatedList: evaluatedValidations,
          logicalOperators: validationType,
        });

        if (isInvalidField) {
          fields.remaining.push(`${targetFieldName}_${invalidKey}=${validationMessage}`);
        }
        fields.all.push(`${targetFieldName}_${invalidKey}=${validationMessage}`);
      } else {
        // Return as remaining field if the target field value is empty.
        if (getIsRequiredFieldEmpty(targetFieldValue)) {
          fields.remaining.push(`${targetFieldName}${identifier}`);
        }

        fields.all.push(`${targetFieldName}${identifier}`);
      }
    }
  }
  return fields;
};

export const getInvalidFieldAndMessage = (field: string) => {
  // Check if the field contains the isInvalid pattern
  if (!field.includes("_isInvalid=")) {
    return {
      fieldName: field,
      customValidationMessage: undefined,
    };
  }

  // Split by '_isInvalid=' to get the field name and message
  const parts = field.split("_isInvalid=");

  if (parts.length !== 2) {
    return {
      fieldName: field,
      customValidationMessage: undefined,
    };
  }

  const fieldName = `${parts[0]}_${invalidKey}`;
  const customValidationMessage = parts[1];

  return {
    fieldName,
    customValidationMessage,
  };
};
